// The "Ethische Etna" sidekick, one living panel that stays beside the
// researcher through every step of the guided intake.
//
// Design intent (see the UX stories in the PR description): the guide is a
// single companion, not a stack of unrelated cards. Everything the guide does
// lives inside ONE bordered panel:
//
//   ┌───────────────────────────────┐
//   │  ● Ethische Etna   (live bar)  │  ← animated header: avatar breathes,
//   │    Kijkt mee · Studieopzet ··· │    status changes per phase, a scan
//   ├───────────────────────────────┤    line sweeps while it "works"
//   │  Voortgang            2/5  ▾   │  ← progress toward submission
//   ├───────────────────────────────┤
//   │  Waar Etna op let             │  ← contextual tips for this step
//   ├───────────────────────────────┤
//   │  💬 Stel een vraag aan Etna    │  ← grounded help, same guide
//   └───────────────────────────────┘
//
// The header is present in EVERY phase (upload, analyzing, wizard, review) so
// the panel reads as the same companion the whole way through.

import { useEffect, useRef, useState } from "react";

import { AgentMessageText } from "../agent-message-text";
import { getAgentResponse, SUGGESTED_QUESTIONS } from "../gids-engine";
import { resolveTip, STEPS, type Tip, tipVisuals } from "./intake-data";

type Phase = "upload" | "analyzing" | "wizard";

/** What the guide is doing right now: drives the header copy and animation. */
type GidsStatus = "waiting" | "reading" | "watching" | "checking" | "done";

interface GidsSidekickProps {
  phase: Phase;
  /** Current wizard step index (wizard phase only). */
  step?: number;
  confirmedCount?: number;
  totalAiFields?: number;
  docsUploaded?: { methodologie: boolean; consent: boolean };
  fieldValues?: Record<string, string>;
  onFocusField?: (fieldId: string) => void;
  /** True while the review step's pre-check is running. */
  reviewChecking?: boolean;
}

export function GidsSidekick({
  phase,
  step = 0,
  confirmedCount = 0,
  totalAiFields = 0,
  docsUploaded = { methodologie: false, consent: false },
  fieldValues = {},
  onFocusField,
  reviewChecking = false,
}: GidsSidekickProps) {
  const isReviewStep = phase === "wizard" && step === STEPS.length - 1;
  const stepData = phase === "wizard" ? STEPS[step] : undefined;

  const status: GidsStatus =
    phase === "upload"
      ? "waiting"
      : phase === "analyzing"
        ? "reading"
        : isReviewStep
          ? reviewChecking
            ? "checking"
            : "done"
          : "watching";

  return (
    <aside style={{ position: "sticky", top: "16px" }}>
      <style>{SIDEKICK_KEYFRAMES}</style>
      <div
        style={{
          border: "1px solid var(--ua-border)",
          borderRadius: "8px",
          overflow: "hidden",
          background: "var(--ua-surface)",
          boxShadow: "var(--ua-shadow)",
        }}
      >
        <GidsHeader status={status} stepLabel={stepData?.navLabel} />

        {/* Body: sections separated by internal dividers, not gaps, so the
            whole thing reads as one panel. */}
        {phase === "wizard" ? (
          <WizardBody
            step={step}
            isReviewStep={isReviewStep}
            confirmedCount={confirmedCount}
            totalAiFields={totalAiFields}
            docsUploaded={docsUploaded}
            fieldValues={fieldValues}
            onFocusField={onFocusField}
          />
        ) : (
          <IntroBody phase={phase} />
        )}
      </div>
    </aside>
  );
}

/* ── The living header ("blue bar") ───────────────────────────────────────── */

const STATUS_COPY: Record<GidsStatus, string> = {
  waiting: "Wacht op je documenten",
  reading: "Je documenten lezen",
  watching: "Kijkt mee",
  checking: "Voorafcheck loopt",
  done: "Voorafcheck afgerond",
};

function GidsHeader({ status, stepLabel }: { status: GidsStatus; stepLabel?: string }) {
  const active = status === "reading" || status === "checking";
  const statusText =
    status === "watching" && stepLabel ? `Kijkt mee · ${stepLabel}` : STATUS_COPY[status];

  return (
    <div
      style={{
        position: "relative",
        background: "linear-gradient(135deg, var(--ua-red), var(--ua-red-active))",
        color: "#fff",
        padding: "14px 16px",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "11px" }}>
        {/* Avatar with a breathing ring: the guide's "heartbeat" */}
        <span
          style={{
            position: "relative",
            width: "34px",
            height: "34px",
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.14)",
              animation: `gids-breathe ${active ? "1.5s" : "3s"} ease-in-out infinite`,
            }}
          />
          <img
            src="/brand/etna-avatar.jpg"
            alt="Ethische Etna"
            style={{
              position: "relative",
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontWeight: 700,
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "7px",
            }}
          >
            Ethische Etna
            <span
              style={{
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                background: "rgba(255,255,255,0.16)",
                borderRadius: "3px",
                padding: "1px 5px",
              }}
            >
              AI
            </span>
          </p>
          <p
            style={{
              margin: "1px 0 0",
              fontSize: "12px",
              color: "rgba(255,255,255,0.72)",
              display: "flex",
              alignItems: "center",
              gap: "2px",
            }}
          >
            {statusText}
            {active && <ThinkingDots />}
          </p>
        </div>
      </div>

      {/* Scan line: sweeps across the base of the header while the guide is
          actively working (reading docs, running the pre-check). */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "2px",
          overflow: "hidden",
          background: active ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
        }}
      >
        {active && (
          <span
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "40%",
              background: "linear-gradient(90deg, transparent, rgba(126,190,255,0.9), transparent)",
              animation: "gids-scan 1.6s ease-in-out infinite",
            }}
          />
        )}
      </span>
    </div>
  );
}

function ThinkingDots() {
  return (
    <span style={{ display: "inline-flex", gap: "2px", marginLeft: "2px" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: "3px",
            height: "3px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.85)",
            animation: `gids-dot 1.2s ease-in-out ${i * 0.18}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

/* ── Body: intro (before the wizard opens) ────────────────────────────────── */

function IntroBody({ phase }: { phase: Phase }) {
  const steps = [
    { icon: "✦", text: "Vult je aanvraag alvast in op basis van je documenten" },
    { icon: "◆", text: "Let onderweg op de EA SHW Tip Sheets bij elke stap" },
    { icon: "✓", text: "Doet een voorafcheck vóór je indient" },
  ];
  return (
    <>
      <Section first>
        <SectionLabel>{phase === "analyzing" ? "Etna is bezig" : "Zo helpt Etna je"}</SectionLabel>
        <div style={{ display: "grid", gap: "9px", marginTop: "10px" }}>
          {steps.map((s) => (
            <div key={s.text} style={{ display: "flex", gap: "9px", alignItems: "flex-start" }}>
              <span
                aria-hidden
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  background: "#eef2f9",
                  border: "1px solid #c5d0e8",
                  color: "var(--ua-blue)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: 900,
                  flexShrink: 0,
                  marginTop: "1px",
                }}
              >
                {s.icon}
              </span>
              <span style={{ fontSize: "12.5px", color: "var(--ua-navy)", lineHeight: 1.45 }}>
                {s.text}
              </span>
            </div>
          ))}
        </div>
      </Section>
      <InflowHelp />
    </>
  );
}

/* ── Body: wizard steps + review ──────────────────────────────────────────── */

function WizardBody({
  step,
  isReviewStep,
  confirmedCount,
  totalAiFields,
  docsUploaded,
  fieldValues,
  onFocusField,
}: {
  step: number;
  isReviewStep: boolean;
  confirmedCount: number;
  totalAiFields: number;
  docsUploaded: { methodologie: boolean; consent: boolean };
  fieldValues: Record<string, string>;
  onFocusField?: (fieldId: string) => void;
}) {
  const stepData = STEPS[step];
  const tips = stepData?.tips ?? [];
  const fieldLabels = new Map((stepData?.fields ?? []).map((f) => [f.id, f.label]));
  const anchoredTips = tips.filter((t) => t.fieldId && fieldLabels.has(t.fieldId));
  const generalTips = tips.filter((t) => !t.fieldId);

  return (
    <>
      {/* Progress toward submission. On the review step the full checklist
          lives in the main column, so we hide it here to avoid duplication. */}
      {!isReviewStep && (
        <ProgressSection
          stepsCompleted={step}
          confirmedCount={confirmedCount}
          totalAiFields={totalAiFields}
          docsUploaded={docsUploaded}
        />
      )}

      {/* Contextual guidance */}
      {isReviewStep ? (
        <Section first={false}>
          <SectionLabel>Voorafcheck</SectionLabel>
          <p
            style={{
              margin: "8px 0 0",
              fontSize: "12.5px",
              color: "var(--ua-muted)",
              lineHeight: 1.5,
            }}
          >
            Etna controleert je volledige dossier in de hoofdkolom. Aandachtspunten houden je
            aanvraag niet tegen.
          </p>
        </Section>
      ) : tips.length === 0 ? (
        <Section first={false}>
          <p style={{ margin: 0, fontSize: "12.5px", color: "var(--ua-muted)", lineHeight: 1.5 }}>
            Geen tips bij deze stap: vul rustig verder aan.
          </p>
        </Section>
      ) : (
        <Section first={false}>
          <SectionLabel>Waar Etna op let bij deze stap</SectionLabel>
          {anchoredTips.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <TipOverviewList
                tips={anchoredTips}
                fieldLabels={fieldLabels}
                fieldValues={fieldValues}
                onFocusField={onFocusField}
              />
            </div>
          )}
          {generalTips.length > 0 && (
            <div
              style={{
                display: "grid",
                gap: "8px",
                marginTop: anchoredTips.length ? "10px" : "10px",
              }}
            >
              {generalTips.map((tip, i) => (
                <TipCard key={`${step}-${tip.id}`} tip={tip} index={i} />
              ))}
            </div>
          )}
        </Section>
      )}

      <InflowHelp />
    </>
  );
}

/* ── Panel section primitives ─────────────────────────────────────────────── */

function Section({ children, first }: { children: React.ReactNode; first: boolean }) {
  return (
    <div
      style={{
        padding: "14px 16px",
        borderTop: first ? "none" : "1px solid var(--ua-border)",
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        fontSize: "10px",
        fontWeight: 700,
        color: "var(--ua-muted)",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </p>
  );
}

/* ── Progress section (submission readiness, with a live bar) ─────────────── */

function ProgressSection({
  stepsCompleted,
  confirmedCount,
  totalAiFields,
  docsUploaded,
}: {
  stepsCompleted: number;
  confirmedCount: number;
  totalAiFields: number;
  docsUploaded: { methodologie: boolean; consent: boolean };
}) {
  const items = [
    {
      label: "Methodologie-bijlage geüpload",
      done: docsUploaded.methodologie,
      why: "Verplichte bijlage (Luik D): beschrijft opzet en werving.",
    },
    {
      label: "Informed consent-bijlage geüpload",
      done: docsUploaded.consent,
      why: "Onvolledige consent is de #1 oorzaak van herwerk (Tip Sheet 2 & 3).",
    },
    {
      label: `AI-velden bevestigd (${confirmedCount}/${totalAiFields})`,
      done: totalAiFields > 0 && confirmedCount >= totalAiFields,
      why: "Elke AI-suggestie wordt apart door jou bevestigd, geen bulk-akkoord.",
    },
    {
      label: "Risicoanalyse ingevuld",
      done: stepsCompleted >= 4,
      why: "Luik C: minderjarigen, kwetsbaarheid, opnames, vergoeding.",
    },
    {
      label: "Voorafcheck uitgevoerd",
      done: stepsCompleted >= 5,
      why: "Etna scant je dossier op onduidelijkheden vóór indienen.",
    },
  ];
  const doneCount = items.filter((item) => item.done).length;
  const complete = doneCount === items.length;
  const [open, setOpen] = useState(false);

  return (
    <div style={{ padding: "14px 16px", borderTop: "none" }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "10px",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <span
          style={{
            fontSize: "10px",
            fontWeight: 700,
            color: "var(--ua-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Voortgang naar indienen
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: complete ? "var(--ua-success)" : "var(--ua-navy)",
            }}
          >
            {doneCount}/{items.length}
          </span>
          <span
            style={{
              fontSize: "11px",
              color: "var(--ua-muted)",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.15s ease",
            }}
          >
            ▾
          </span>
        </span>
      </button>

      {open && (
        <div style={{ display: "grid", gap: "10px", marginTop: "12px" }}>
          {items.map((item) => (
            <div key={item.label} style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
              <Check done={item.done} />
              <span style={{ minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: item.done ? "var(--ua-navy)" : "var(--ua-muted)",
                    fontWeight: item.done ? 700 : 400,
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: "11px",
                    color: "var(--ua-muted)",
                    lineHeight: 1.4,
                    marginTop: "1px",
                  }}
                >
                  {item.why}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Check({ done }: { done: boolean }) {
  return (
    <span
      style={{
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        background: done ? "var(--ua-success)" : "var(--ua-border)",
        color: done ? "#fff" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "9px",
        fontWeight: 900,
        flexShrink: 0,
        marginTop: "2px",
        transition: "background 0.3s ease",
      }}
    >
      {done ? "✓" : ""}
    </span>
  );
}

/* ── Tips: overview list that jumps to fields ─────────────────────────────── */

function TipOverviewList({
  tips,
  fieldLabels,
  fieldValues,
  onFocusField,
}: {
  tips: Tip[];
  fieldLabels: Map<string, string>;
  fieldValues: Record<string, string>;
  onFocusField?: (fieldId: string) => void;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--ua-border)",
        borderRadius: "6px",
        overflow: "hidden",
        background: "var(--ua-white)",
      }}
    >
      {tips.map((tip, i) => {
        const state = resolveTip(tip, tip.fieldId ? fieldValues[tip.fieldId] : undefined);
        const t = tipVisuals(state.tone);
        return (
          <button
            key={tip.id}
            type="button"
            onClick={() => tip.fieldId && onFocusField?.(tip.fieldId)}
            style={{
              display: "flex",
              gap: "10px",
              width: "100%",
              textAlign: "left",
              padding: "10px 12px",
              border: "none",
              borderTop: i === 0 ? "none" : "1px solid var(--ua-border)",
              background: "none",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <TipDot color={t.accent} icon={t.icon} />
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 700,
                  color: "var(--ua-navy)",
                  lineHeight: 1.35,
                }}
              >
                {state.title}
              </span>
              <span
                style={{
                  display: "block",
                  marginTop: "2px",
                  fontSize: "11px",
                  color: "var(--ua-blue)",
                  fontWeight: 700,
                }}
              >
                → {fieldLabels.get(tip.fieldId ?? "")}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TipDot({ color, icon }: { color: string; icon: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        background: color,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: 900,
        flexShrink: 0,
        marginTop: "1px",
      }}
    >
      {icon}
    </span>
  );
}

function TipCard({ tip, index }: { tip: Tip; index: number }) {
  const t = tipVisuals(tip.tone);
  return (
    <div
      style={{
        border: "1px solid var(--ua-border)",
        borderLeft: `3px solid ${t.accent}`,
        borderRadius: "6px",
        background: "var(--ua-white)",
        padding: "11px 13px",
        opacity: 0,
        animation: "gids-pop 0.4s ease forwards",
        animationDelay: `${index * 120}ms`,
      }}
    >
      <span
        style={{
          fontSize: "10px",
          fontWeight: 700,
          color: t.accent,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {t.label}
      </span>
      <p
        style={{
          margin: "5px 0 5px",
          fontWeight: 700,
          fontSize: "12.5px",
          color: "var(--ua-navy)",
        }}
      >
        {tip.title}
      </p>
      <p
        style={{ margin: "0 0 7px", fontSize: "12px", color: "var(--ua-muted)", lineHeight: 1.55 }}
      >
        {tip.body}
      </p>
      <span
        style={{
          fontSize: "10.5px",
          fontWeight: 700,
          background: "#eef2f9",
          border: "1px solid #c5d0e8",
          color: "var(--ua-navy)",
          borderRadius: "999px",
          padding: "1px 8px",
          display: "inline-block",
        }}
      >
        {tip.source}
      </span>
    </div>
  );
}

/* ── In-flow grounded help: the guide's chat, as the panel footer ────────── */

function InflowHelp() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [thread, setThread] = useState<{ q: string; text: string; sources: string[] }[]>([]);
  const [typing, setTyping] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [thread, typing]);

  function ask(question: string) {
    if (!question.trim() || typing) {
      return;
    }
    setInput("");
    setTyping(true);
    setThread((prev) => [...prev, { q: question, text: "", sources: [] }]);
    setTimeout(() => {
      const res = getAgentResponse(question);
      setThread((prev) => {
        const next = [...prev];
        next[next.length - 1] = { q: question, text: res.text, sources: res.tipSheets };
        return next;
      });
      setTyping(false);
    }, 900);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          textAlign: "left",
          borderTop: "1px solid var(--ua-border)",
          background: "var(--ua-bg)",
          padding: "13px 16px",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <span style={{ fontSize: "16px" }}>💬</span>
        <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--ua-navy)" }}>
          Stel een vraag aan Etna
          <span
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: 400,
              color: "var(--ua-muted)",
            }}
          >
            Krijg antwoord zonder deze pagina te verlaten
          </span>
        </span>
      </button>
    );
  }

  return (
    <div style={{ borderTop: "1px solid var(--ua-border)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: "1px solid var(--ua-border)",
          background: "var(--ua-bg)",
        }}
      >
        <span
          style={{
            fontSize: "10px",
            fontWeight: 700,
            background: "var(--ua-navy)",
            color: "#fff",
            borderRadius: "4px",
            padding: "2px 7px",
          }}
        >
          AI · gebaseerd op Tip Sheets
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            background: "none",
            border: "none",
            fontSize: "16px",
            lineHeight: 1,
            color: "var(--ua-muted)",
            cursor: "pointer",
          }}
          aria-label="Sluiten"
        >
          ×
        </button>
      </div>

      <div style={{ maxHeight: "320px", overflowY: "auto", padding: "12px 14px" }}>
        {thread.length === 0 ? (
          <div style={{ display: "grid", gap: "6px" }}>
            <p style={{ margin: "0 0 4px", fontSize: "12px", color: "var(--ua-muted)" }}>
              Veelgestelde vragen:
            </p>
            {SUGGESTED_QUESTIONS.slice(0, 4).map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => ask(q)}
                style={{
                  textAlign: "left",
                  background: "var(--ua-white)",
                  border: "1px solid var(--ua-border)",
                  borderRadius: "6px",
                  padding: "7px 10px",
                  fontSize: "12px",
                  color: "var(--ua-navy)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  lineHeight: 1.35,
                }}
              >
                {q}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gap: "14px" }}>
            {thread.map((m, i) => (
              <div key={i}>
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "var(--ua-navy)",
                    textAlign: "right",
                  }}
                >
                  {m.q}
                </p>
                {m.text ? (
                  <>
                    <AgentMessageText text={m.text} compact />
                    {m.sources.length > 0 && (
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "8px" }}
                      >
                        {m.sources.map((s) => (
                          <span
                            key={s}
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              background: "#eef2f9",
                              border: "1px solid #c5d0e8",
                              color: "var(--ua-navy)",
                              borderRadius: "999px",
                              padding: "2px 8px",
                            }}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      color: "var(--ua-muted)",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Etna zoekt het antwoord
                    <DarkThinkingDots />
                  </p>
                )}
              </div>
            ))}
            <div ref={threadEndRef} />
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          ask(input);
        }}
        style={{
          display: "flex",
          gap: "6px",
          padding: "10px 12px",
          borderTop: "1px solid var(--ua-border)",
          background: "var(--ua-bg)",
        }}
      >
        <input
          type="text"
          aria-label="Stel een vraag aan Etna"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Typ je vraag…"
          disabled={typing}
          style={{ ...fieldStyle, fontSize: "16px", padding: "7px 10px" }}
        />
        <button
          type="submit"
          className="btn btn--primary"
          disabled={!input.trim() || typing}
          style={{ fontSize: "12px", padding: "7px 12px", whiteSpace: "nowrap" }}
        >
          Vraag
        </button>
      </form>
    </div>
  );
}

function DarkThinkingDots() {
  return (
    <span style={{ display: "inline-flex", gap: "3px" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: "4px",
            height: "4px",
            borderRadius: "50%",
            background: "var(--ua-muted)",
            animation: `gids-dot 1.2s ease-in-out ${i * 0.18}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

const fieldStyle: React.CSSProperties = {
  fontFamily: "inherit",
  fontSize: "14px",
  color: "var(--ua-navy)",
  background: "var(--ua-white)",
  border: "1px solid var(--ua-border)",
  borderRadius: "var(--ua-radius)",
  padding: "9px 12px",
  width: "100%",
};

const SIDEKICK_KEYFRAMES = `
  @keyframes gids-breathe {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50%      { transform: scale(1.35); opacity: 0; }
  }
  @keyframes gids-scan {
    0%   { left: -40%; }
    100% { left: 100%; }
  }
  @keyframes gids-dot {
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40%           { opacity: 1; transform: translateY(-1px); }
  }
  @keyframes gids-pop {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    [style*="gids-breathe"], [style*="gids-scan"], [style*="gids-dot"],
    [style*="gids-pop"] {
      animation: none !important;
    }
  }
`;
